import { useEffect, useRef, useState } from 'react';

// Manages the WebSocket connection to the tracking server, sending the
// driver's live GPS position at a regular interval while active.
// Also captures "heading" (direction of travel, 0-360°) when the browser
// provides it — used for the driving-mode map rotation and truck icon
// rotation features. Heading is only reliable while actually moving;
// GPS can't determine direction of travel while stationary, so it's
// often null at low speeds — we simply keep the last known good value
// in that case rather than resetting it to null.
export function useLocationTracking(enabled) {
  const [isConnected, setIsConnected] = useState(false);
  const [currentPosition, setCurrentPosition] = useState(null);
  const [heading, setHeading] = useState(null);
  const wsRef = useRef(null);
  const watchIdRef = useRef(null);

  useEffect(() => {
    if (!enabled) {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      setIsConnected(false);
      return;
    }

    const token = localStorage.getItem('token');
    const ws = new WebSocket(process.env.REACT_APP_TRACKING_WS_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: 'auth', token }));
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'auth_success') {
        setIsConnected(true);
      } else if (data.type === 'error') {
        console.error('Tracking error:', data.message);
        setIsConnected(false);
      }
    };

    ws.onclose = () => setIsConnected(false);
    ws.onerror = () => setIsConnected(false);

    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const { latitude, longitude, heading: gpsHeading } = position.coords;
        setCurrentPosition({ lat: latitude, lng: longitude });

        if (gpsHeading !== null && !isNaN(gpsHeading)) {
          setHeading(gpsHeading);
        }

        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'location', lat: latitude, lng: longitude }));
        }
      },
      (error) => console.error('Geolocation error:', error),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
    );

    return () => {
      ws.close();
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, [enabled]);

  return { isConnected, currentPosition, heading };
}